import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { DiscAlbumIcon, HomeIcon, LibraryIcon, ListMusicIcon, Music2Icon, PlusIcon, SearchIcon } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
export const navItems = [{
  to: '/',
  label: 'Home',
  Icon: HomeIcon
}, {
  to: '/search',
  label: 'Search',
  Icon: SearchIcon
}, {
  to: '/now-playing',
  label: 'Now Playing',
  Icon: Music2Icon
}, {
  to: '/library',
  label: 'Your Library',
  Icon: LibraryIcon
}];
export function Sidebar() {
  const {
    favorites,
    playlists,
    createPlaylist
  } = useLibrary();
  const [composing, setComposing] = useState(false);
  const [name, setName] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createPlaylist(name);
    setName('');
    setComposing(false);
  };
  return <aside className="hidden w-60 flex-none flex-col border-r border-ink-line bg-ink-panel/80 px-5 py-6 backdrop-blur md:flex" data-id="element-137" data-mp="37eebzpkjqpoi">
      <div className="flex items-center gap-2.5" data-id="element-138" data-mp="1lxkjluu1wjkz">
        <DiscAlbumIcon className="h-6 w-6 text-gold" aria-hidden="true" data-id="element-139" data-mp="1qqnf48ie233t" />
        <span className="font-display text-xl tracking-tight text-cream" data-id="element-140" data-mp="1cz8ww3wb25xt">Noctuzan</span>
      </div>
      <p className="mt-2 text-xs italic text-muted" data-id="element-141" data-mp="1h0aygrdp2u2y">late-night radio, on demand</p>

      <nav aria-label="Main" className="mt-9" data-id="element-142" data-mp="1gy7x5b73deix">
        <ul className="space-y-1" data-id="element-143" data-mp="24r81feniih22">
          {navItems.map(({
          to,
          label,
          Icon
        }) => <li key={to} data-id="element-144" data-mp="29phxdrh29loq">
              <NavLink to={to} end={to === '/'} className={({
            isActive
          }) => `flex items-center gap-3 rounded-sm px-2.5 py-2 text-sm transition-colors duration-150 ease-night focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft ${isActive ? 'bg-ink-raised text-gold' : 'text-muted hover:text-cream'}`} data-id="element-145" data-mp="29opzub3wldvt">
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-146" data-mp="2qz3n10qcmre4" />
                {label}
              </NavLink>
            </li>)}
        </ul>
      </nav>

      <div className="mt-8 flex min-h-0 flex-1 flex-col" data-id="element-147" data-mp="2m1tqz28gfhhb">
        <div className="flex items-center justify-between" data-id="element-148" data-mp="2h7g0ne8ng0ji">
          <h2 className="text-xs uppercase tracking-wide text-muted" data-id="element-149" data-mp="2d7fa4v89zyw3">Playlists</h2>
          <button type="button" onClick={() => setComposing(c => !c)} aria-label="New playlist" aria-expanded={composing} className="rounded-full p-1 text-muted transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-150" data-mp="2882qskgm59y9">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-151" data-mp="1u9w29uxk0mb9" />
          </button>
        </div>

        <AnimatePresence initial={false} data-id="element-152" data-mp="3sne492l25udc">
          {composing && <motion.form onSubmit={submit} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-153" data-mp="3om2mtkcc8fg8">
              <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="Name this set…" aria-label="New playlist name" className="mt-2 w-full rounded border border-ink-line bg-ink-base px-2 py-1.5 text-xs text-cream placeholder:text-muted focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-154" data-mp="1k56665ou0cey" />
            </motion.form>}
        </AnimatePresence>

        <ul className="mt-3 min-h-0 flex-1 space-y-0.5 overflow-y-auto scroll-thin pr-1" data-id="element-155" data-mp="2bvkfrpptb2kt">
          {playlists.length === 0 && !composing && <li className="text-xs leading-relaxed text-muted" data-id="element-156" data-mp="2gtc5cd2ziclu">
              Build a set from any track&apos;s <span className="text-cream" data-id="element-157" data-mp="2fe9fjfwk656u">＋</span> menu.
            </li>}
          {playlists.map(playlist => <li key={playlist.id} data-id="element-158" data-mp="2ag80d6296q0n">
              <NavLink to={`/playlist/${playlist.id}`} className={({
            isActive
          }) => `flex items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm transition-colors duration-150 ease-night focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft ${isActive ? 'bg-ink-raised text-gold' : 'text-muted hover:text-cream'}`} data-id="element-159" data-mp="2odwkgbohxt4n">
                <ListMusicIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-160" data-mp="381m4txu3sr3u" />
                <span className="truncate" data-id="element-161" data-mp="334dirm5u42xb">{playlist.name}</span>
                <span className="ml-auto flex-none text-[11px] tabular-nums" data-id="element-162" data-mp="3h2k6wakzeojs">{playlist.tracks.length}</span>
              </NavLink>
            </li>)}
        </ul>
      </div>

      <div className="mt-5 flex-none border-t border-ink-line pt-5 text-xs leading-relaxed text-muted" data-id="element-163" data-mp="3vpiyc5ju9iyi">
        <p data-id="element-164" data-mp="4j7p3m4odbw3">
          <span className="text-cream" data-id="element-165" data-mp="4jrajctjaiev">{favorites.length}</span> saved {favorites.length === 1 ? 'track' : 'tracks'}
        </p>
        <p className="mt-2" data-id="element-166" data-mp="3mnsycbbw5bp4">Previews run ~30 seconds, courtesy of the iTunes Search API.</p>
      </div>
    </aside>;
}